/** Derive https origin from `REACT_APP_WS` (e.g. `wss://api.example.com/addata` → `https://api.example.com`). */
function apiBaseFromWsEnv(): string | null {
  const ws = process.env.REACT_APP_WS?.trim();
  if (!ws) return null;
  try {
    const normalized = ws.replace(/^ws(s)?:/i, (_, s) => (s ? "https:" : "http:"));
    return new URL(normalized).origin.replace(/\/$/, "");
  } catch {
    return null;
  }
}

/** Base URL for REST API (same host as deployed app, or dev server port). */
export const getApiBase = (): string => {
  const fromEnv = process.env.REACT_APP_API_BASE?.trim();
  if (fromEnv) return fromEnv.replace(/\/$/, "");
  const fromWs = apiBaseFromWsEnv();
  if (fromWs) return fromWs;
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/$/, "");
  }
  return "";
};

/**
 * Full URLs to try for GET /api/leaderboard (in order).
 * Many deployments proxy `/api/*` on the **page origin** (e.g. CloudFront) while `REACT_APP_WS`
 * points at a bare socket host — `getApiBase()` alone would miss the REST route. We try same-origin first.
 */
export type LeaderboardGame = "addata" | "literature" | "rummy";

export function leaderboardFetchUrls(limit: number, game: LeaderboardGame): string[] {
  const q = `limit=${encodeURIComponent(String(limit))}`;
  const gameQ = `game=${encodeURIComponent(game)}`;
  const path = `/api/leaderboard?${q}&${gameQ}`;
  const explicit = process.env.REACT_APP_API_BASE?.trim();
  if (explicit) {
    return [`${explicit.replace(/\/$/, "")}${path}`];
  }
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin.replace(/\/$/, "")
      : "";
  const fromWs = apiBaseFromWsEnv();
  const urls: string[] = [];
  if (origin) urls.push(`${origin}${path}`);
  if (fromWs && fromWs !== origin) urls.push(`${fromWs}${path}`);
  if (!origin && fromWs) urls.push(`${fromWs}${path}`);
  return Array.from(new Set(urls));
}
